import React, { useState, useEffect } from "react";

const seedData = {
  "Broccoli": { gramsPerTray: 12, soak: "No" },
  "Radish": { gramsPerTray: 30, soak: "No" },
  "Sunflower": { gramsPerTray: 100, soak: "Yes - 6 to 8 hours" },
  "Pea": { gramsPerTray: 200, soak: "Yes - 8 to 12 hours" },
  "Amaranth": { gramsPerTray: 6, soak: "No" },
  "Arugula": { gramsPerTray: 10, soak: "No" },
  "Red Cabbage": { gramsPerTray: 10, soak: "No" },
  "Borage": { gramsPerTray: 15, soak: "Yes - 4 to 6 hours" },
  "Coriander": { gramsPerTray: 30, soak: "Yes - 4 to 6 hours" },
  "Parsley": { gramsPerTray: 8, soak: "No" },
  "Basil": { gramsPerTray: 7, soak: "No" },
  "Kohlrabi": { gramsPerTray: 12, soak: "No" },
  "Wheatgrass": { gramsPerTray: 120, soak: "Yes - 8 to 12 hours" },
  "Kale": { gramsPerTray: 12, soak: "No" },
  "Nasturtium": { gramsPerTray: 20, soak: "Yes - 8 to 12 hours" }
};

const traySizes = ["10x20", "Half Tray", "Custom"];
const progressLabels = [
  "🥣 Prepare Tray",
  "💧 Soak Seeds",
  "🌾 Sow & Cover",
  "🌑 Dark Phase",
  "💡 Light Phase",
  "✂️ Harvest"
];

export default function App() {
  const [selectedSeed, setSelectedSeed] = useState("Broccoli");
  const [selectedTray, setSelectedTray] = useState("10x20");
  const [customWeight, setCustomWeight] = useState(1);
  const [progress, setProgress] = useState(() => {
    const saved = localStorage.getItem("eym-progress");
    return saved ? parseInt(saved, 10) : 0;
  });

  useEffect(() => {
    localStorage.setItem("eym-progress", progress);
  }, [progress]);

  const handleSeedChange = (e) => setSelectedSeed(e.target.value);
  const handleTrayChange = (e) => setSelectedTray(e.target.value);
  const handleWeightChange = (e) => setCustomWeight(Number(e.target.value));
  const handleProgressChange = (e) => setProgress(Number(e.target.value));

  const seedInfo = seedData[selectedSeed];
  const weight = selectedTray === "10x20"
    ? seedInfo.gramsPerTray
    : selectedTray === "Half Tray"
      ? seedInfo.gramsPerTray / 2
      : seedInfo.gramsPerTray * customWeight;

  const downloadLog = () => {
    const log = `EYM Grow Log\nSeed: ${selectedSeed}\nTray Size: ${selectedTray}\nWeight: ${weight.toFixed(1)}g\nCurrent Stage: ${progressLabels[progress]}`;
    const blob = new Blob([log], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `EYM_Grow_Log_${selectedSeed}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
    className="p-4 max-w-xl mx-auto"
    style={{ fontFamily: 'Poppins, sans-serif' }}
    >
      <h1 className="text-2xl font-bold text-green-700 mb-4">EYM Local Grower App</h1>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">📈 Progress Tracker</h2>
        <input
          type="range"
          min="0"
          max={progressLabels.length - 1}
          value={progress}
          onChange={handleProgressChange}
          className="w-full"
        />
        <div className="text-sm mt-2">Current step: <strong>{progressLabels[progress]}</strong></div>
        <img
          src={`https://eymicrogreens.nl/images/stages/step-${progress}.jpg`}
          alt={progressLabels[progress]}
          className="mt-3 rounded shadow-md"
        />
        <button
          onClick={downloadLog}
          className="mt-4 bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700"
        >
          📄 Download Grow Log
        </button>
      </section>
<section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">📊 Seed Tray Calculator</h2>
        <div className="space-y-2 text-sm">
          <div>
            <label>Seed Variety: </label>
            <select value={selectedSeed} onChange={handleSeedChange} className="border px-2 py-1">
              {Object.keys(seedData).map((seed) => (
                <option key={seed} value={seed}>{seed}</option>
              ))}
            </select>
          </div>

          <div>
            <label>Tray Size: </label>
            <select value={selectedTray} onChange={handleTrayChange} className="border px-2 py-1">
              {traySizes.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          {selectedTray === "Custom" && (
            <div>
              <label>Custom weight multiplier (e.g. 1.5 for 1.5x bigger tray): </label>
              <input type="number" value={customWeight} onChange={handleWeightChange} className="border px-2 py-1 w-20" />
            </div>
          )}

          <div className="mt-2">
            <strong>Recommended weight:</strong> {weight.toFixed(1)} grams
          </div>
        </div>
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">🌱 Step-by-Step Instructions</h2>
        <ol className="list-decimal ml-5 text-sm space-y-1">
          <li>Choose clean trays and ensure drainage.</li>
          <li>Pre-soak seeds if required: {seedInfo.soak}</li>
          <li>Evenly spread <strong>{weight}g</strong> of {selectedSeed} seeds per tray.</li>
          <li>Stack or cover the trays for 2–4 days (dark phase).</li>
          <li>Move to light when germinated. Keep moist.</li>
          <li>Harvest when microgreens are 7–10 cm tall.</li>
        </ol>
      </section>

      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">🛠️ Tools & Environment Tips</h2>
        <ul className="list-disc ml-5 text-sm space-y-1">
          <li>Use organic, untreated seeds for best results.</li>
          <li>Keep room temperature around 18–22°C.</li>
          <li>Humidity around 40–60% is ideal.</li>
          <li>Use a small scale with 0.01g precision.</li>
          <li>LED grow lights with timer help optimize growth.</li>
        </ul>
      </section>

      <footer className="text-xs text-gray-500 text-center mt-8">
        Developed by Eat Your Microgreens · <a href="https://eymicrogreens.nl" className="underline">eymicrogreens.nl</a>
      </footer>
    </div>
  );
}
